import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  adminStats,
  adminReservations,
  adminReferrals,
  adminContacts,
  adminLogout,
  adminMe,
  getAdminToken,
  api,
} from "../../api";
import {
  LogOut,
  Calendar,
  Users,
  DollarSign,
  Inbox,
  Tag,
  Search,
  CalendarPlus,
  Camera,
  X,
} from "lucide-react";
import Logo from "../../components/Logo";
import { formatPrice } from "../../i18n";

const STATUS_COLORS = {
  confirmed: "bg-green-500/15 text-green-400 border-green-500/30",
  awaiting_payment: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  request_pending: "bg-blue-500/15 text-blue-400 border-blue-500/30",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("reservations");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeReservation, setActiveReservation] = useState(null);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login");
      return;
    }
    adminMe().catch(() => {
      navigate("/admin/login");
    });
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r, ref, c] = await Promise.all([
        adminStats().catch(() => null),
        adminReservations(statusFilter ? { status: statusFilter } : {}).catch(() => []),
        adminReferrals().catch(() => []),
        adminContacts().catch(() => []),
      ]);
      setStats(s);
      setReservations(r);
      setReferrals(ref);
      setContacts(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLogout = async () => {
    await adminLogout();
    navigate("/admin/login");
  };

  const filteredReservations = reservations.filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (r.full_name || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.car_name || "").toLowerCase().includes(s) ||
      (r.flight_number || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard-page">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-4">
            <Logo size="sm" showTagline={false} />
            <span className="text-label hidden md:inline">Admin dashboard</span>
          </Link>
          <button
            onClick={onLogout}
            className="btn-outline py-2.5 px-4 text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          {[
            { icon: Inbox, label: "Reservations (all)", value: stats?.total ?? "—" },
            { icon: Calendar, label: "Pickups today", value: stats?.today_pickups ?? "—" },
            {
              icon: DollarSign,
              label: "Revenue (paid)",
              value: stats ? formatPrice(stats.revenue_confirmed_usd) : "—",
            },
            { icon: Tag, label: "Referrals issued", value: stats?.referrals ?? "—" },
          ].map((s, i) => (
            <div key={i} className="bg-[#0d0d0d] border border-white/10 p-5">
              <s.icon size={18} className="text-[#FFB400]" />
              <p className="text-label mt-3">{s.label}</p>
              <p className="font-heading text-3xl font-bold mt-1">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-6">
          {[
            { id: "reservations", label: `Reservations (${reservations.length})` },
            { id: "referrals", label: `Referrals (${referrals.length})` },
            { id: "contacts", label: `Contacts (${contacts.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-xs font-mono-ui uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[#FFB400] text-white"
                  : "border-transparent text-zinc-500 hover:text-white"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Reservations */}
        {tab === "reservations" && (
          <div data-testid="admin-reservations-tab">
            <div className="flex flex-wrap gap-3 mb-5">
              <div className="relative flex-1 min-w-[240px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  className="movix-input pl-9 text-sm"
                  placeholder="Search by name, email, car, flight…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="admin-search"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="movix-input text-sm cursor-pointer w-auto min-w-[180px]"
                data-testid="admin-status-filter"
              >
                <option value="" className="bg-[#0a0a0a]">All statuses</option>
                <option value="request_pending" className="bg-[#0a0a0a]">Request pending</option>
                <option value="awaiting_payment" className="bg-[#0a0a0a]">Awaiting payment</option>
                <option value="confirmed" className="bg-[#0a0a0a]">Confirmed (paid)</option>
              </select>
            </div>

            <div className="bg-[#0d0d0d] border border-white/10 overflow-x-auto" data-testid="admin-reservations-table">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] text-zinc-500 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="text-left p-4">Customer</th>
                    <th className="text-left p-4">Car</th>
                    <th className="text-left p-4">Pick-up</th>
                    <th className="text-left p-4">Return</th>
                    <th className="text-left p-4">Flight</th>
                    <th className="text-left p-4">Total</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="p-6 text-zinc-500 text-center">Loading…</td></tr>
                  ) : filteredReservations.length === 0 ? (
                    <tr><td colSpan={8} className="p-6 text-zinc-500 text-center">No reservations yet.</td></tr>
                  ) : (
                    filteredReservations.map((r) => (
                      <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="p-4">
                          <div className="text-white font-medium">{r.full_name}</div>
                          <div className="text-xs text-zinc-500">{r.email}</div>
                          <div className="text-xs text-zinc-500">{r.phone}</div>
                        </td>
                        <td className="p-4 text-zinc-300">{r.car_name}</td>
                        <td className="p-4 text-zinc-300">{r.pickup_date}</td>
                        <td className="p-4 text-zinc-300">{r.return_date}</td>
                        <td className="p-4 text-zinc-300">
                          {r.airport_pickup ? (
                            <span className="text-[#FFB400] font-mono-ui text-xs">
                              ✈ {r.flight_number || "—"}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                        <td className="p-4 text-white font-medium">{formatPrice(r.total_amount)}</td>
                        <td className="p-4">
                          <span
                            className={`inline-block px-2 py-1 text-[10px] font-mono-ui uppercase tracking-widest border ${
                              STATUS_COLORS[r.status] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"
                            }`}
                          >
                            {(r.status || "").replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setActiveReservation({ ...r, action: "extend" })}
                              className="text-zinc-400 hover:text-[#FFB400] p-1.5 border border-white/10 hover:border-[#FFB400]/50 transition-colors"
                              title="Extend rental"
                              data-testid={`admin-extend-${r.id}`}
                            >
                              <CalendarPlus size={14} />
                            </button>
                            <button
                              onClick={() => setActiveReservation({ ...r, action: "handover" })}
                              className="text-zinc-400 hover:text-[#FFB400] p-1.5 border border-white/10 hover:border-[#FFB400]/50 transition-colors"
                              title="Verified by Movix handover"
                              data-testid={`admin-handover-${r.id}`}
                            >
                              <Camera size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "referrals" && (
          <div className="bg-[#0d0d0d] border border-white/10 overflow-x-auto" data-testid="admin-referrals-table">
            <table className="w-full text-sm">
              <thead className="bg-[#0a0a0a] text-zinc-500 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-left p-4">Code</th>
                  <th className="text-left p-4">Owner</th>
                  <th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Discount</th>
                  <th className="text-left p-4">Used</th>
                  <th className="text-left p-4">Created</th>
                </tr>
              </thead>
              <tbody>
                {referrals.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-zinc-500 text-center">No referrals issued yet.</td></tr>
                ) : (
                  referrals.map((r) => (
                    <tr key={r.code} className="border-t border-white/5">
                      <td className="p-4 font-mono-ui text-[#FFB400]">{r.code}</td>
                      <td className="p-4 text-white">{r.name || "—"}</td>
                      <td className="p-4 text-zinc-400">{r.email}</td>
                      <td className="p-4 text-white">{formatPrice(r.discount_usd)}</td>
                      <td className="p-4 text-white font-medium">{r.redemptions}</td>
                      <td className="p-4 text-zinc-500 text-xs">{r.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "contacts" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-contacts-list">
            {contacts.length === 0 ? (
              <p className="text-zinc-500 col-span-2 text-center py-12">No contact messages yet.</p>
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="bg-[#0d0d0d] border border-white/10 p-5">
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <p className="text-white font-medium">{c.name}</p>
                      <p className="text-xs text-zinc-500">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                    </div>
                    <p className="text-xs text-zinc-600 font-mono-ui">{c.created_at?.slice(0, 10)}</p>
                  </div>
                  <p className="text-sm text-[#FFB400] mt-3 font-medium">{c.subject || "—"}</p>
                  <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{c.message}</p>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {activeReservation && (
        <ReservationActionModal
          reservation={activeReservation}
          onClose={() => setActiveReservation(null)}
          onSaved={() => {
            setActiveReservation(null);
            load();
          }}
        />
      )}
    </div>
  );
};

const ReservationActionModal = ({ reservation, onClose, onSaved }) => {
  const [tab, setTab] = useState(reservation.action || "extend");
  const [newReturn, setNewReturn] = useState(reservation.return_date);
  const [extending, setExtending] = useState(false);
  const [handover, setHandover] = useState({
    kind: "pickup",
    odometer_miles: "",
    fuel_pct: 100,
    notes: "",
    photo_urls: "",
  });
  const [saving, setSaving] = useState(false);

  const doExtend = async () => {
    setExtending(true);
    try {
      const res = await api.post(
        `/admin/reservations/${reservation.id}/extend`,
        { new_return_date: newReturn }
      );
      toast.success(
        `Extended by ${res.data.extra_days} days — new total $${res.data.new_total}`
      );
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setExtending(false);
    }
  };

  const doHandover = async () => {
    setSaving(true);
    try {
      const photos = handover.photo_urls
        .split(/[\n,]/)
        .map((u) => u.trim())
        .filter(Boolean);
      await api.post(`/admin/reservations/${reservation.id}/handover`, {
        kind: handover.kind,
        odometer_miles: parseFloat(handover.odometer_miles || "0"),
        fuel_pct: parseInt(handover.fuel_pct || "100", 10),
        notes: handover.notes || undefined,
        photo_urls: photos,
      });
      toast.success("Handover recorded · Verified by Movix");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="reservation-action-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#0d0d0d] border border-white/10 max-w-xl w-full p-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-label text-[#FFB400]">{reservation.car_name}</p>
            <h3 className="font-heading text-2xl text-white mt-1">{reservation.full_name}</h3>
            <p className="text-xs text-zinc-500 font-mono-ui mt-1">{reservation.id}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="flex gap-2 mt-6 border-b border-white/10">
          <button
            onClick={() => setTab("extend")}
            className={`px-4 py-2.5 text-xs font-mono-ui uppercase tracking-widest border-b-2 -mb-px ${
              tab === "extend" ? "border-[#FFB400] text-white" : "border-transparent text-zinc-500"
            }`}
            data-testid="modal-tab-extend"
          >
            <CalendarPlus size={12} className="inline mr-1.5 -mt-0.5" />
            Extend
          </button>
          <button
            onClick={() => setTab("handover")}
            className={`px-4 py-2.5 text-xs font-mono-ui uppercase tracking-widest border-b-2 -mb-px ${
              tab === "handover" ? "border-[#FFB400] text-white" : "border-transparent text-zinc-500"
            }`}
            data-testid="modal-tab-handover"
          >
            <Camera size={12} className="inline mr-1.5 -mt-0.5" />
            Verified handover
          </button>
        </div>

        {tab === "extend" ? (
          <div className="mt-6 space-y-4" data-testid="modal-extend-form">
            <div>
              <label className="text-label block mb-2">Current return</label>
              <p className="text-white font-mono-ui">{reservation.return_date}</p>
            </div>
            <div>
              <label className="text-label block mb-2">New return date *</label>
              <input
                type="date"
                className="movix-input"
                value={newReturn}
                min={reservation.return_date}
                onChange={(e) => setNewReturn(e.target.value)}
                data-testid="extend-new-return"
              />
            </div>
            <button
              onClick={doExtend}
              disabled={extending || newReturn === reservation.return_date}
              className="btn-primary w-full justify-center disabled:opacity-60"
              data-testid="extend-submit"
            >
              {extending ? "Extending…" : "Extend rental"}
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="modal-handover-form">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setHandover({ ...handover, kind: "pickup" })}
                className={`p-3 border ${
                  handover.kind === "pickup"
                    ? "border-[#FFB400] bg-[#FFB400]/10"
                    : "border-white/10"
                } text-sm font-mono-ui uppercase tracking-widest`}
                data-testid="handover-kind-pickup"
              >
                Pickup
              </button>
              <button
                onClick={() => setHandover({ ...handover, kind: "return" })}
                className={`p-3 border ${
                  handover.kind === "return"
                    ? "border-[#FFB400] bg-[#FFB400]/10"
                    : "border-white/10"
                } text-sm font-mono-ui uppercase tracking-widest`}
                data-testid="handover-kind-return"
              >
                Return
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-label block mb-2">Odometer (mi) *</label>
                <input
                  type="number"
                  className="movix-input"
                  value={handover.odometer_miles}
                  onChange={(e) => setHandover({ ...handover, odometer_miles: e.target.value })}
                  data-testid="handover-odometer"
                />
              </div>
              <div>
                <label className="text-label block mb-2">Fuel (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="movix-input"
                  value={handover.fuel_pct}
                  onChange={(e) => setHandover({ ...handover, fuel_pct: e.target.value })}
                  data-testid="handover-fuel"
                />
              </div>
            </div>
            <div>
              <label className="text-label block mb-2">Photo URLs (one per line)</label>
              <textarea
                rows={3}
                className="movix-input"
                placeholder="https://… (paste image hosting URLs)"
                value={handover.photo_urls}
                onChange={(e) => setHandover({ ...handover, photo_urls: e.target.value })}
                data-testid="handover-photos"
              />
            </div>
            <div>
              <label className="text-label block mb-2">Notes</label>
              <textarea
                rows={2}
                className="movix-input"
                value={handover.notes}
                onChange={(e) => setHandover({ ...handover, notes: e.target.value })}
                data-testid="handover-notes"
              />
            </div>
            <button
              onClick={doHandover}
              disabled={saving || !handover.odometer_miles}
              className="btn-primary w-full justify-center disabled:opacity-60"
              data-testid="handover-submit"
            >
              {saving ? "Saving…" : "Save handover"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
